import { useState, useEffect, useCallback, useMemo } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useQuery } from "@tanstack/react-query";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { SelectedReportingPeriodAtom, ReportingPeriodAtom } from "@/store/ReportingPeriodStateAtom";
import { getHoldings } from "@/api/Holdings";
import { getReportingPeriods } from "@/api/ReportingPeriods";
import { HoldingWithInstrumentAndAccount } from "@/types";
import { ReportingPeriod } from "@/types/ReportingPeriod";

export interface HoldingComparison {
  instrumentKey: string;
  instrumentName: string;
  primaryHolding?: HoldingWithInstrumentAndAccount;
  secondaryHolding?: HoldingWithInstrumentAndAccount;
  quantityChange?: number;
  amountChange?: number;
  percentageChange?: number;
  status: 'new' | 'removed' | 'changed' | 'unchanged';
}

export function useHoldingsComparisonController() {
  const [primaryPeriod, setPrimaryPeriod] = useState<ReportingPeriod | null>(null);
  const [secondaryPeriod, setSecondaryPeriod] = useState<ReportingPeriod | null>(null);
  const [primaryHoldings, setPrimaryHoldings] = useState<HoldingWithInstrumentAndAccount[]>([]);
  const [secondaryHoldings, setSecondaryHoldings] = useState<HoldingWithInstrumentAndAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const selectedFund = useAtomValue(SelectedFundAtom);
  const selectedReportingPeriod = useAtomValue(SelectedReportingPeriodAtom);
  const [reportingPeriods, setReportingPeriods] = useAtom(ReportingPeriodAtom);

  // Fetch reporting periods when component loads
  const {
    data: periods = [],
    isLoading: isLoadingPeriods,
  } = useQuery({
    queryKey: ["reportingPeriods", selectedFund?.fundId],
    queryFn: async () => {
      console.log('Fetching reporting periods for fund:', selectedFund?.fundId);
      const data = await getReportingPeriods(
        selectedFund?.fundId?.toString() || ""
      );
      console.log('Reporting periods fetched:', data.length, 'periods');
      setReportingPeriods(data);
      return data;
    },
    enabled: !!selectedFund?.fundId,
  });

  // Set primary period to current selected period by default
  useEffect(() => {
    if (selectedReportingPeriod && !primaryPeriod) {
      setPrimaryPeriod(selectedReportingPeriod);
    }
  }, [selectedReportingPeriod, primaryPeriod]);

  // Fetch holdings for both periods
  const fetchHoldings = useCallback(async () => {
    console.log('fetchHoldings called', { selectedFund, primaryPeriod, secondaryPeriod });
    
    if (!selectedFund || !primaryPeriod) {
      console.log('No fund or primary period selected, clearing holdings');
      setPrimaryHoldings([]);
      setSecondaryHoldings([]);
      return;
    }

    setIsLoading(true);
    try {
      console.log('Fetching primary holdings for period:', primaryPeriod.reportingPeriodId);
      const primaryData = await getHoldings(
        selectedFund.fundId,
        parseInt(primaryPeriod.reportingPeriodId)
      );
      console.log('Primary holdings fetched:', primaryData.length, 'items');
      setPrimaryHoldings(primaryData);

      if (secondaryPeriod) {
        console.log('Fetching secondary holdings for period:', secondaryPeriod.reportingPeriodId);
        const secondaryData = await getHoldings(
          selectedFund.fundId,
          parseInt(secondaryPeriod.reportingPeriodId)
        );
        console.log('Secondary holdings fetched:', secondaryData.length, 'items');
        setSecondaryHoldings(secondaryData);
      } else {
        console.log('No secondary period, clearing secondary holdings');
        setSecondaryHoldings([]);
      }
    } catch (error) {
      console.error("Error fetching holdings for comparison:", error);
      setPrimaryHoldings([]);
      setSecondaryHoldings([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedFund, primaryPeriod, secondaryPeriod]);

  useEffect(() => {
    fetchHoldings();
  }, [fetchHoldings]);

  // Compute comparison data
  const comparisonData = useMemo((): HoldingComparison[] => {
    if (!primaryPeriod) return [];

    const primaryMap = new Map<string, HoldingWithInstrumentAndAccount>();
    const secondaryMap = new Map<string, HoldingWithInstrumentAndAccount>();

    // Create maps for efficient lookup
    primaryHoldings.forEach(holding => {
      primaryMap.set(holding.instrument.instrumentKey, holding);
    });

    secondaryHoldings.forEach(holding => {
      secondaryMap.set(holding.instrument.instrumentKey, holding);
    });

    // Get all unique instrument keys
    const allInstrumentKeys = new Set([
      ...primaryMap.keys(),
      ...secondaryMap.keys()
    ]);

    const comparisons: HoldingComparison[] = [];

    allInstrumentKeys.forEach(instrumentKey => {
      const primaryHolding = primaryMap.get(instrumentKey);
      const secondaryHolding = secondaryMap.get(instrumentKey);

      let status: HoldingComparison['status'] = 'unchanged';
      let quantityChange: number | undefined;
      let amountChange: number | undefined;
      let percentageChange: number | undefined;

      if (primaryHolding && secondaryHolding) {
        // Both periods have this holding - check for changes
        quantityChange = primaryHolding.quantity - secondaryHolding.quantity;
        amountChange = primaryHolding.baseAmount - secondaryHolding.baseAmount;
        
        if (secondaryHolding.baseAmount !== 0) {
          percentageChange = ((primaryHolding.baseAmount - secondaryHolding.baseAmount) / Math.abs(secondaryHolding.baseAmount)) * 100;
        }

        status = (quantityChange !== 0 || amountChange !== 0) ? 'changed' : 'unchanged';
      } else if (primaryHolding && !secondaryHolding) {
        // New holding in primary period
        status = 'new';
      } else if (!primaryHolding && secondaryHolding) {
        // Holding removed from primary period
        status = 'removed';
      }

      comparisons.push({
        instrumentKey,
        instrumentName: primaryHolding?.instrument.name || secondaryHolding?.instrument.name || instrumentKey,
        primaryHolding,
        secondaryHolding,
        quantityChange,
        amountChange,
        percentageChange,
        status
      });
    });

    // Filter by search query if provided
    if (searchQuery.trim()) {
      const search = searchQuery.toLowerCase();
      return comparisons.filter(comparison =>
        comparison.instrumentName.toLowerCase().includes(search) ||
        comparison.instrumentKey.toLowerCase().includes(search)
      );
    }

    return comparisons;
  }, [primaryHoldings, secondaryHoldings, primaryPeriod, searchQuery]);

  const clearSecondaryPeriod = useCallback(() => {
    setSecondaryPeriod(null);
  }, []);

  return {
    primaryPeriod,
    secondaryPeriod,
    primaryHoldings,
    secondaryHoldings,
    comparisonData,
    isLoading: isLoading || isLoadingPeriods,
    searchQuery,
    reportingPeriods: periods,
    setPrimaryPeriod,
    setSecondaryPeriod,
    clearSecondaryPeriod,
    setSearchQuery,
  };
} 